/**
 * useMicBreath
 *
 * Captures microphone input and derives a normalised breath amplitude (0-1)
 * updated ~30fps. Used to drive the breath waveform in the Pranayama screen.
 *
 * Returns:
 *   amplitude    — smoothed 0-1 breath level
 *   isActive     — mic is running
 *   isSupported  — getUserMedia available
 *   start()      — request mic + begin analysis
 *   stop()       — release mic
 *   error        — string or null
 */
import { useState, useCallback, useRef, useEffect } from 'react';

const FFT_SIZE      = 512;
const SMOOTH_FACTOR = 0.75; // 0=fast, 1=static
const UPDATE_MS     = 33;   // ~30fps

export const useMicBreath = () => {
  const [amplitude, setAmplitude]   = useState(0);
  const [isActive, setIsActive]     = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [error, setError]           = useState(null);

  const streamRef    = useRef(null);
  const contextRef   = useRef(null);
  const analyserRef  = useRef(null);
  const dataRef      = useRef(null);
  const intervalRef  = useRef(null);
  const smoothRef    = useRef(0);
  const mountedRef   = useRef(true);

  useEffect(() => {
    setIsSupported(!!(navigator.mediaDevices?.getUserMedia));
    return () => { mountedRef.current = false; };
  }, []);

  const stop = useCallback(() => {
    if (intervalRef.current)  { clearInterval(intervalRef.current); intervalRef.current = null; }
    if (streamRef.current)    { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    if (contextRef.current)   { contextRef.current.close().catch(() => {}); contextRef.current = null; }
    analyserRef.current = null;
    dataRef.current     = null;
    smoothRef.current   = 0;
    if (mountedRef.current) { setIsActive(false); setAmplitude(0); }
  }, []);

  const start = useCallback(async () => {
    if (!isSupported) { setError('Microphone not supported in this browser.'); return; }
    // Guard: stop any existing mic before starting a new one
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    if (contextRef.current) { contextRef.current.close().catch(() => {}); contextRef.current = null; }
    smoothRef.current = 0;
    setError(null);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
        video: false,
      });

      const ctx      = new (window.AudioContext || window.webkitAudioContext)();
      const source   = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize            = FFT_SIZE;
      analyser.smoothingTimeConstant = 0;
      source.connect(analyser);

      const data = new Uint8Array(analyser.frequencyBinCount);

      streamRef.current  = stream;
      contextRef.current = ctx;
      analyserRef.current = analyser;
      dataRef.current    = data;

      if (mountedRef.current) setIsActive(true);

      intervalRef.current = setInterval(() => {
        if (!analyserRef.current || !mountedRef.current) return;
        analyserRef.current.getByteTimeDomainData(data);

        // RMS of the waveform - picks up breath as low-frequency amplitude
        let sum = 0;
        for (let i = 0; i < data.length; i++) {
          const v = (data[i] / 128) - 1; // centre on 0
          sum += v * v;
        }
        const rms = Math.sqrt(sum / data.length);

        // Smooth and normalise (breath RMS roughly 0-0.15, map to 0-1)
        const norm = Math.min(rms / 0.12, 1);
        smoothRef.current = SMOOTH_FACTOR * smoothRef.current + (1 - SMOOTH_FACTOR) * norm;
        setAmplitude(smoothRef.current);
      }, UPDATE_MS);

    } catch (err) {
      const msg = err.name === 'NotAllowedError'
        ? 'Microphone permission denied. Tap the lock icon in your browser to allow.'
        : 'Could not access microphone.';
      if (mountedRef.current) setError(msg);
    }
  }, [isSupported]);

  // Cleanup on unmount
  useEffect(() => () => { mountedRef.current = false; stop(); }, [stop]);

  return { amplitude, isActive, isSupported, error, start, stop };
};
